/**
 * Handles the custom .product-info__variant-btn size/option pickers.
 * Selects the matching variant and updates price, add-to-cart state,
 * dynamic checkout and the hidden variant id input.
 */
class ProductVariantButtons {
  constructor(container) {
    this.container = container;
    this.productInfo = container.closest('.product-info');

    const dataEl = this.productInfo.querySelector('[data-product-variants]');
    this.variants = JSON.parse(dataEl.textContent);
    this.moneyFormat = dataEl.dataset.moneyFormat || '${{amount}}';

    this.priceBlock = this.productInfo.querySelector('#PriceBlock');
    this.variantIdInput = this.productInfo.querySelector('#VariantIdInput');
    this.atcButton = this.productInfo.querySelector('.product-info__atc-btn');
    this.dynamicCheckout = this.productInfo.querySelector('#DynamicCheckout');

    this.container.addEventListener('click', this.onButtonClick.bind(this));
  }

  onButtonClick(event) {
    const button = event.target.closest('.product-info__variant-btn');
    if (!button) return;

    const group = button.closest('.product-info__variant-group');
    group.querySelectorAll('.product-info__variant-btn').forEach((btn) => {
      const isSelected = btn === button;
      btn.classList.toggle('is-selected', isSelected);
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    const label = group.querySelector('.product-info__variant-label span');
    if (label) label.textContent = button.dataset.value;

    this.updateVariant();
  }

  getSelectedOptions() {
    return Array.from(this.container.querySelectorAll('.product-info__variant-group')).map((group) => {
      const selected = group.querySelector('.product-info__variant-btn.is-selected');
      return selected ? selected.dataset.value : null;
    });
  }

  updateVariant() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.variants.find((v) =>
      v.options.every((value, index) => value === selectedOptions[index])
    );

    if (!variant) return;

    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
      this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updatePrice(variant);
    this.updateAddToCart(variant);
    this.updateDynamicCheckout(variant);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    const withComma = amount.replace('.', ',');

    return this.moneyFormat
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{ amount_with_comma_separator }}', withComma)
      .replace('{{amount}}', amount)
      .replace('{{ amount }}', amount);
  }

  updatePrice(variant) {
    if (!this.priceBlock) return;

    const onSale = variant.compare_at_price > variant.price;

    if (onSale) {
      const discountPct = Math.floor(
        ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
      );
      this.priceBlock.innerHTML = `
        <span class="product-info__price product-info__price--sale">${this.formatMoney(variant.price)}</span>
        <span class="product-info__compare-price">${this.formatMoney(variant.compare_at_price)}</span>
        <span class="product-info__save-badge">Save ${discountPct}%</span>
      `;
    } else {
      this.priceBlock.innerHTML = `<span class="product-info__price">${this.formatMoney(variant.price)}</span>`;
    }
  }

  updateAddToCart(variant) {
    if (!this.atcButton) return;

    this.atcButton.disabled = !variant.available;

    const span = this.atcButton.querySelector('span');
    const label = variant.available
      ? '<i class="fa-regular fa-bag-shopping"></i> Add to Cart'
      : 'Sold Out';
    if (span) {
      span.innerHTML = label;
    } else {
      this.atcButton.querySelector('.loading__spinner')?.remove();
      this.atcButton.innerHTML = `<span>${label}</span>`;
    }
  }

  updateDynamicCheckout(variant) {
    if (!this.dynamicCheckout) return;

    this.dynamicCheckout.classList.toggle('hidden', !variant.available);
    window.Shopify?.PaymentButton?.init();
  }
}

document.querySelectorAll('.product-info__variants').forEach((container) => {
  new ProductVariantButtons(container);
});
